import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs/operators';

import { InterventionActions } from 'app/interventions/store/actions/interventions';

import type { Intervention } from '../../../interventions/models/intervention';
import { HttpInterventionGateway } from '../../../interventions/services/intervention-gateway.service';
import { Site, SiteId } from '../../../site-detail/models/Site';
import { getSiteById } from '../../../site-detail/store/selectors/site';
import { getOwnerFullName } from '../../../site-owner/utils/owner.util';
import { useToaster } from '../../composables/use-toaster';
import { UISocketDataMessage } from '../../models/UISocket';
import { AppState } from '../../store/app-state';
import { SocketAction, SocketActionHandler } from '../SocketActionHandler';

interface UISocketInterventionAccessGrantedMessage extends UISocketDataMessage {
    interventionSessionId: string;
    siteId: SiteId;
}

@Injectable()
export class InterventionGrantedHandler implements SocketActionHandler {
    readonly action: SocketAction = 'interventionAccessGranted';

    private interventionGateway = inject(HttpInterventionGateway);
    private store = inject<Store<AppState>>(Store);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();

    handle(message: UISocketInterventionAccessGrantedMessage): void {
        this.interventionGateway.getInterventions(message.siteId).subscribe((interventions) => {
            this.store.dispatch(InterventionActions.siteInterventionsLoaded({ interventions }));

            const intervention: Intervention = interventions.find((i) => i.sessionId === message.interventionSessionId);
            if (!intervention) {
                return;
            }

            this.store
                .select(getSiteById(message.siteId))
                .pipe(take(1), filter(Boolean))
                .subscribe((site: Site) => {
                    this.toaster.success({
                        title: this.translate.instant('TOASTER_ACCESS_GRANTED_HEADER', {
                            userName: getOwnerFullName(site.owners[0]),
                        }),
                        action: {
                            label: this.translate.instant('TOASTER_ACCESS_GRANTED_START_INTERVENTION'),
                            run: () => this.router.navigate([`/gateway/${site.gatewayId}`]),
                        },
                        duration: 0,
                    });
                });
        });
    }
}
